import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/pop-ui/pop-toast";
import { getCurrentLanguageInClient } from "@/lib/utils.client";
import { useTurnstile } from "@/providers/turnstile-provider";
const PUBLIC_TURNSTILE_ENABLED_FALSE = "PUBLIC_TURNSTILE_ENABLED_FALSE";

// 声明window.turnstile类型（可放到全局类型声明文件，临时写在此处）
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        "error-callback"?: () => void;
        appearance?: "always";
        theme?: "light";
        hl?: string; // Widget language per Cloudflare supported codes
      }) => string;
      remove: (widgetId: string) => void;
      execute: (widgetId: string) => void;
    };
    __cfTurnstileLoaded?: boolean;
  }
}

/**
 * Cloudflare Turnstile hook
 * @param options 传入配置选项
 */
export function useCfTurnstile(options: {
  containerId?: string;
  errorMessage?: string;
  appearance?: "always";
  language?: string; // Cloudflare-supported language (e.g., 'en', 'zh-cn', 'zh-tw')
  // When true, bypasses PUBLIC_TURNSTILE_ENABLED check for debugging
  ignoreGlobalFlag?: boolean;
  onError?: () => void; // Callback for turnstile errors
} = {}): {
  loading: boolean;
  isLoadingToken: boolean;
  renderTurnstile: () => Promise<string>;
  getCfTurnstileToken: () => string | undefined;
  removeTurnstile: () => void;
  formatToken: (token: string | undefined) => string;
  isTurnstileEnabled: boolean;
  isWidgetReady: boolean;
} {
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const widgetIdRef = useRef<string | null>(null);
  const containerId = options.containerId || "cf-turnstile-container";

  // Get global SDK state
  const {
    isSdkReady,
    isSdkLoading,
    sdkError
  } = useTurnstile();

  // Use only the managed site key
  const sitekey = process.env.NEXT_PUBLIC_CF_TURNSTILE_MANAGED_SITE_KEY || "";
  const {
    t
  } = useTranslation();
  const isTurnstileEnabled = Boolean(isSdkReady);

  // Map our app language codes to Cloudflare-supported codes
  const mapToCloudflareLang = (lang: string | undefined): string | undefined => {
    if (!lang) return undefined;
    const lower = lang.toLowerCase();
    if (lower.startsWith("zh-hant")) return "zh-tw";
    if (lower.startsWith("zh-hans")) return "zh-cn";
    if (lower.startsWith("zh")) return "zh";
    if (lower.startsWith("en")) return "en";
    return undefined;
  };

  // Compute the language to pass to Turnstile (options override user language)
  const resolvedLanguage = mapToCloudflareLang(options.language || getCurrentLanguageInClient());

  // 移除widget（放在前面以便下方依赖可引用）
  const removeTurnstile = useCallback(() => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
  }, []);

  // 渲染Turnstile，返回Promise
  const renderTurnstile = useCallback(() => {
    setIsLoadingToken(true);
    return new Promise<string>((resolve, reject) => {
      if (!isTurnstileEnabled) {
        setIsLoadingToken(false);
        resolve(PUBLIC_TURNSTILE_ENABLED_FALSE);
        return;
      }
      if (!window.turnstile || !isSdkReady) {
        setIsLoadingToken(false);
        // Show specific message for turnstile SDK loading failure
        toast.error(t("gateway.error.475"));
        reject(new Error("Turnstile not available"));
        return;
      }
      if (!sitekey) {
        setIsLoadingToken(false);
        // Show specific message for turnstile SDK loading failure
        toast.error(t("gateway.error.475"));
        reject(new Error("Turnstile sitekey missing"));
        return;
      }
      removeTurnstile();
      // Hint language via data attribute on container as well
      try {
        const containerEl = document.getElementById(containerId);
        if (containerEl && resolvedLanguage) {
          containerEl.setAttribute("data-language", resolvedLanguage);
        }
      } catch {}
      widgetIdRef.current = window.turnstile.render(`#${containerId}`, {
        sitekey: sitekey,
        theme: "light",
        appearance: options.appearance || "always",
        ...(resolvedLanguage && {
          hl: resolvedLanguage
        }),
        callback: (token: string) => {
          setIsLoadingToken(false);
          // Call global callback if it exists
          if ((window as any).turnstileSuccessCallback) {
            ;
            (window as any).turnstileSuccessCallback(token);
          }
          resolve(token);
        },
        "error-callback": () => {
          setIsLoadingToken(false);
          // Call parent error callback if provided, otherwise show toast
          if (options.onError) {
            options.onError();
          } else {
            // Show specific message for dialog verification failure (unusual activity detected)
            toast.error(t("gateway.error.476"));
          }
          // Call global error callback if it exists
          if ((window as any).turnstileErrorCallback) {
            ;
            (window as any).turnstileErrorCallback();
          }
          reject(new Error("Turnstile verification failed"));
        }
      });
      // Optionally inspect the DOM later if needed
    }).catch(error => {
      setIsLoadingToken(false);
      // Don't show additional error message since we already showed the specific one above
      throw error;
    });
  }, [containerId, sitekey, options.appearance, options.onError, isTurnstileEnabled, t, removeTurnstile, resolvedLanguage, isSdkReady]);

  // 获取token
  const getCfTurnstileToken = useCallback(() => {
    // Try multiple selectors to find the Turnstile token
    const tokenSelectors = [".cf-turnstile input[name='cf-turnstile-response']", "input[name='cf-turnstile-response']", "[name='cf-turnstile-response']"];
    for (const selector of tokenSelectors) {
      const token_0 = document.querySelector(selector) as HTMLInputElement;
      if (token_0?.value) {
        return token_0.value;
      }
    }
    return undefined;
  }, []);

  // 格式化token，避免将假值传给后端
  const formatToken = useCallback((token_1: string | undefined) => {
    if (!token_1 || token_1 === PUBLIC_TURNSTILE_ENABLED_FALSE) {
      return "";
    }
    return token_1;
  }, []);
  return {
    loading: isSdkLoading,
    isLoadingToken,
    renderTurnstile,
    getCfTurnstileToken,
    removeTurnstile,
    formatToken,
    isTurnstileEnabled: Boolean(isTurnstileEnabled),
    isWidgetReady: isSdkReady
  };
}